import { Link } from 'react-router-dom'
import { 
  Globe, 
  DollarSign, 
  Percent, 
  MapPin, 
  Settings as SettingsIcon,
  FileText
} from 'lucide-react'

const SettingsPage = () => {
  const settingsSections = [
    {
      title: 'Configuration des pays',
      description: 'Gérer les pays où l\'application est hébergée',
      icon: Globe,
      href: '/countries',
      color: 'bg-blue-500',
      items: [
        { name: 'Liste des pays', description: 'Activer/désactiver les pays' },
        { name: 'Devises par pays', description: 'Configurer les devises par pays' },
        { name: 'Langues par pays', description: 'Définir les langues supportées' },
      ]
    },
    {
      title: 'Gestion des devises',
      description: 'Configurer les devises et taux de change',
      icon: DollarSign,
      href: '/currencies',
      color: 'bg-green-500',
      items: [
        { name: 'Devises supportées', description: 'Gérer les devises disponibles' },
        { name: 'Taux de change', description: 'Mettre à jour les taux de conversion' },
        { name: 'Format monétaire', description: 'Configurer l\'affichage des prix' },
      ]
    },
    {
      title: 'Configuration des taxes',
      description: 'Gérer les taxes par pays, province et état',
      icon: Percent,
      href: '/tax-rates',
      color: 'bg-red-500',
      items: [
        { name: 'Taxes par pays', description: 'Configurer les taxes nationales' },
        { name: 'Provinces/États', description: 'Gérer les régions et leurs taxes' },
        { name: 'Règles fiscales', description: 'Définir les règles de calcul' },
      ]
    },
    {
      title: 'Géolocalisation',
      description: 'Configurer les régions et zones',
      icon: MapPin,
      href: '/regions',
      color: 'bg-purple-500',
      items: [
        { name: 'Provinces canadiennes', description: 'QC, ON, BC, AB, etc.' },
        { name: 'États américains', description: 'CA, NY, TX, FL, etc.' },
        { name: 'Autres régions', description: 'Régions européennes et autres' },
      ]
    },
    {
      title: 'Paramètres généraux',
      description: 'Configuration générale de l\'application',
      icon: SettingsIcon,
      href: '/settings/general',
      color: 'bg-gray-500',
      items: [
        { name: 'Paramètres système', description: 'Configuration de base' },
        { name: 'Notifications', description: 'Paramètres des notifications' },
        { name: 'Sécurité', description: 'Paramètres de sécurité' },
      ]
    }
  ]

  return (
    <div className="animate-in">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Paramétrage de l'application</h1>
        <p className="text-gray-600 mt-2">
          Configurez les paramètres de l'application pour différents pays, devises et taxes.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {settingsSections.map((section) => {
          const Icon = section.icon
          return (
            <Link
              key={section.title}
              to={section.href}
              className="bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all duration-200"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 rounded-lg ${section.color}`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {section.items.length} configurations
                  </span>
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{section.title}</h3>
                <p className="text-sm text-gray-600 mb-4">{section.description}</p>
                
                <div className="space-y-3">
                  {section.items.map((item, index) => (
                    <div key={index} className="flex items-center text-sm">
                      <div className="w-2 h-2 rounded-full bg-gray-300 mr-3"></div>
                      <div>
                        <div className="font-medium text-gray-900">{item.name}</div>
                        <div className="text-gray-500 text-xs">{item.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <span className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                    Configurer →
                  </span>
                </div>
              </div>
            </Link>
          )
        })}
      </div>

      {/* Informations importantes */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start">
          <FileText className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
          <div>
            <h3 className="text-lg font-semibold text-blue-900 mb-2">Informations importantes</h3>
            <ul className="text-blue-800 space-y-2">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Les changements de configuration peuvent prendre quelques minutes pour être propagés</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Assurez-vous de configurer les taxes correctement pour chaque pays/région</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Les devises doivent être activées avant de pouvoir être utilisées</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Testez toujours les configurations dans un environnement de test avant la production</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SettingsPage
